<template>
  <div v-if="open" class="tm-backdrop" role="dialog" aria-modal="true" aria-labelledby="tm-send-title" @click.self="close">
    <div class="tm-modal">
      <div class="tm-modal-header">
        <div>
          <h5 id="tm-send-title" class="mb-0 font-weight-bold text-white"><i class="fe fe-video mr-2"></i>Send for Telemedicine</h5>
          <small class="text-white" style="opacity:.8;">Book a virtual consultation with a telemedicine doctor</small>
        </div>
        <button type="button" class="tm-close" aria-label="Close" @click="close"><i class="fe fe-x"></i></button>
      </div>

      <form class="tm-modal-body" @submit.prevent="submit">
        <!-- Patient -->
        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="tm-search" class="tm-label">Search enrollee</label>
            <input id="tm-search" v-model="searchkey" type="search" class="form-control" placeholder="ID number, name or phone…"
              @input="searchPatient" />
          </div>
          <div class="form-group col-md-6">
            <label for="tm-patient" class="tm-label">Enrollee <span class="text-danger">*</span>
              <span v-if="searching" class="text-muted small ml-1">searching…</span>
            </label>
            <select id="tm-patient" v-model="patient" class="form-control" required>
              <option :value="null">-- select from results --</option>
              <option v-for="p in results" :key="p.type + p.id" :value="p">{{ p.user_query || p.full_name }}</option>
            </select>
          </div>
        </div>

        <!-- Doctor -->
        <div class="form-group" v-if="specializationNames.length">
          <label for="tm-specfilter" class="tm-label">Specialization needed <span class="text-muted small">(optional)</span></label>
          <select id="tm-specfilter" v-model="specialization" class="form-control">
            <option value="">Any specialization</option>
            <option v-for="name in specializationNames" :key="name" :value="name">{{ name }}</option>
          </select>
        </div>
        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="tm-facility" class="tm-label">Telemedicine provider <span class="text-danger">*</span></label>
            <select id="tm-facility" v-model="facilityId" class="form-control" required>
              <option value="">-- select --</option>
              <option v-for="f in matchingFacilities" :key="f.id" :value="f.id">{{ f.agency_name }}{{ specLabel(f) }}</option>
            </select>
            <small class="text-muted" v-if="loaded && facilities.length && !matchingFacilities.length">
              No provider lists {{ specialization }}.
            </small>
            <small class="text-muted" v-if="loaded && !facilities.length">
              No telemedicine providers are registered for this agency yet.
            </small>
          </div>
          <div class="form-group col-md-6">
            <label for="tm-doctor" class="tm-label">Doctor <span class="text-muted small">(optional)</span></label>
            <select id="tm-doctor" v-model="professionalId" class="form-control" :disabled="!doctors.length">
              <option value="">Any available doctor</option>
              <option v-for="d in doctors" :key="d.id" :value="d.id">{{ d.firstname }} {{ d.lastname }}<template v-if="d.job_title"> ({{ d.job_title }})</template>{{ specLabel(d) }}</option>
            </select>
          </div>
        </div>

        <!-- Schedule: only the doctors' free times can be picked -->
        <SlotPicker v-model="slot" :facility-id="facilityId || null" :professional-id="professionalId || null" />

        <div class="form-group">
          <label for="tm-reason" class="tm-label">Reason for consultation <span class="text-danger">*</span></label>
          <textarea id="tm-reason" v-model="reason" rows="3" class="form-control" required
            placeholder="Symptoms, history and what the doctor should review…"></textarea>
        </div>

        <div v-if="errors.length" class="alert alert-danger small py-2">
          <div v-for="e in errors" :key="e">{{ e }}</div>
        </div>

        <div class="d-flex justify-content-end" style="gap: 10px;">
          <button type="button" class="btn btn-light" @click="close">Cancel</button>
          <button type="submit" class="btn tm-btn" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm mr-1" role="status"></span>
            <i v-else class="fe fe-send mr-1"></i>Send
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
// Shared by Appointments.vue and TelemedicineConsultations.vue. Emits `sent` with the new consultation.
let searchTimer = null;

function storedAgency() {
  try {
    return JSON.parse(localStorage.getItem("agency") || "null");
  } catch (e) {
    return null;
  }
}

import SlotPicker from "./SlotPicker.vue";

export default {
  name: "SendTelemedicine",
  components: { SlotPicker },
  props: {
    open: { type: Boolean, default: false },
  },
  data() {
    return {
      agency: storedAgency(),
      searchkey: "",
      searching: false,
      results: [],
      patient: null,
      facilities: [],
      loaded: false,
      facilityId: "",
      professionalId: "",
      specialization: "",
      slot: null,
      reason: "",
      saving: false,
      errors: [],
      today: new Date().toISOString().slice(0, 10),
    };
  },
  computed: {
    // Every specialization offered by the providers or their doctors.
    specializationNames() {
      const names = new Set();
      this.facilities.forEach((f) => {
        (f.specializations || []).forEach((s) => names.add(s.name));
        (f.doctors || []).forEach((d) => (d.specializations || []).forEach((s) => names.add(s.name)));
      });
      return Array.from(names).sort();
    },
    matchingFacilities() {
      if (!this.specialization) return this.facilities;
      const has = (x) => (x.specializations || []).some((s) => s.name === this.specialization);
      return this.facilities.filter((f) => has(f) || (f.doctors || []).some(has));
    },
    doctors() {
      const f = this.facilities.find((x) => Number(x.id) === Number(this.facilityId));
      return f ? f.doctors || [] : [];
    },
  },
  watch: {
    open(value) {
      if (value && !this.loaded) this.loadFacilities();
    },
    facilityId() {
      this.professionalId = "";
    },
  },
  methods: {
    // " — Cardiology, Paediatrics" after a name in the dropdowns.
    specLabel(x) {
      const names = (x.specializations || []).map((s) => s.name);
      return names.length ? " — " + names.join(", ") : "";
    },
    loadFacilities() {
      this.axios
        .get("/api/v1/auth/telemedicine/facilities")
        .then((res) => (this.facilities = res.data.data || []))
        .finally(() => (this.loaded = true));
    },
    searchPatient() {
      clearTimeout(searchTimer);
      if (!this.searchkey.trim()) {
        this.results = [];
        return;
      }
      searchTimer = setTimeout(() => {
        this.searching = true;
        this.axios
          .post("/api/v1/auth/searchenrollees", { agency_id: this.agency && this.agency.id, request_query: this.searchkey })
          .then((res) => {
            const data = res.data;
            this.results = Array.isArray(data) ? data : (data && data.data) || [];
            if (this.results.length === 1) this.patient = this.results[0];
          })
          .finally(() => (this.searching = false));
      }, 400);
    },
    reset() {
      Object.assign(this.$data, { searchkey: "", results: [], patient: null, facilityId: "", professionalId: "", specialization: "",
        slot: null, reason: "", errors: [] });
    },
    close() {
      this.$emit("close");
    },
    submit() {
      this.errors = [];
      if (!this.patient) {
        this.errors = ["Search and select the enrollee."];
        return;
      }
      if (!this.slot) {
        this.errors = ["Pick one of the free times."];
        return;
      }
      this.saving = true;
      const isDependant = this.patient.type === "Dependent";
      this.axios
        .post("/api/v1/auth/telemedicine/consultations", {
          patient_id: isDependant ? null : this.patient.id,
          dependent_id: isDependant ? this.patient.id : null,
          provider_id: this.facilityId,
          // The doctor of the chosen slot (a free doctor when "any available doctor" was left).
          professional_id: this.slot.professional_id,
          appointDate: this.slot.date,
          appoinTime: this.slot.time,
          reason: this.reason,
        })
        .then((res) => {
          this.$toasted.success(res.data.message || "Sent for telemedicine", { position: "top-center", duration: 3000 });
          this.$emit("sent", res.data.data);
          this.reset();
          this.close();
        })
        .catch((err) => {
          const data = (err.response && err.response.data) || {};
          this.errors = data.errors ? Object.values(data.errors).flat() : [data.message || "Could not send the patient."];
        })
        .finally(() => (this.saving = false));
    },
  },
};
</script>

<style scoped>
.tm-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, .55); z-index: 1050; display: flex; align-items: flex-start; justify-content: center; padding: 40px 12px; overflow-y: auto; }
.tm-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 760px; overflow: hidden; box-shadow: 0 20px 50px rgba(0,0,0,.25); }
.tm-modal-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 20px 24px; background: linear-gradient(135deg, #4c1d95, #7c3aed); }
.tm-close { background: rgba(255,255,255,.15); border: none; border-radius: 8px; color: #fff; width: 32px; height: 32px; }
.tm-modal-body { padding: 20px 24px; }
.tm-label { font-weight: 600; font-size: .85rem; color: #374151; }
.tm-btn { background: linear-gradient(135deg, #4c1d95, #7c3aed); color: #fff; border: none; border-radius: 8px; font-weight: 600; padding: 8px 20px; }
.tm-btn:hover { opacity: .9; color: #fff; }
</style>
